import React, { useState, useEffect } from 'react';
import { Plus, Calendar, MapPin, Users, Video, X, Filter } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { eventsService, Event } from '../services/eventsService';
import { ArtCategory } from '../types';
import { ContentModerationError } from '../lib/contentModeration';

const EventsView: React.FC = () => {
  const { user } = useAuth();
  const { select, locale, t } = useLanguage();
  const categoryLabels: Record<ArtCategory, string> = {
    music: select({ es: 'Música', en: 'Music' }),
    writing: select({ es: 'Escritura', en: 'Writing' }),
    tattoo: select({ es: 'Tatuajes', en: 'Tattoos' }),
    digital_design: select({ es: 'Diseño Digital', en: 'Digital Design' }),
    fashion_design: select({ es: 'Diseño de Moda', en: 'Fashion Design' }),
    photography: select({ es: 'Fotografía', en: 'Photography' }),
    sculpture: select({ es: 'Escultura', en: 'Sculpture' }),
    painting: select({ es: 'Pintura', en: 'Painting' }),
    dance: select({ es: 'Danza', en: 'Dance' }),
    filmmaking: select({ es: 'Cine', en: 'Filmmaking' }),
    makeup: select({ es: 'Maquillaje', en: 'Makeup' }),
  };

  const eventTypeLabels: Record<string, { es: string; en: string }> = {
    exhibition: { es: 'Exposición', en: 'Exhibition' },
    workshop: { es: 'Taller', en: 'Workshop' },
    concert: { es: 'Concierto', en: 'Concert' },
    meetup: { es: 'Encuentro', en: 'Meetup' },
    online: { es: 'Online', en: 'Online' },
  };
  const [activeTab, setActiveTab] = useState('browse');
  const [events, setEvents] = useState<Event[]>([]);
  const [myEvents, setMyEvents] = useState<Event[]>([]);
  const [myRegistrations, setMyRegistrations] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createError, setCreateError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showEventDetails, setShowEventDetails] = useState(false);

  const [filters, setFilters] = useState({
    category: 'all' as ArtCategory | 'all',
    eventType: 'all' as string,
    location: '',
    priceRange: 'all' as string,
    upcoming: true,
  });

  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    category: '' as ArtCategory,
    event_type: 'exhibition' as 'exhibition' | 'workshop' | 'concert' | 'meetup' | 'online',
    start_date: '',
    end_date: '',
    location: '',
    virtual_link: '',
    external_url: '',
    is_external: false,
    price: '',
    max_attendees: '',
    image_url: '',
    banner_url: '',
    tags: '',
    status: 'draft' as 'draft' | 'published',
  });

  useEffect(() => {
    void loadEvents();
    if (user) {
      void loadMyEvents();
      void loadMyRegistrations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, user]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const data = await eventsService.getEvents({
        category: filters.category,
        eventType: filters.eventType,
        location: filters.location,
        upcoming: filters.upcoming,
        limit: 20,
      });
      setEvents(data);
    } catch (err) {
      console.error('Error loading events:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMyEvents = async () => {
    try {
      const data = await eventsService.getMyEvents();
      setMyEvents(data);
    } catch (err) {
      console.error('Error loading my events:', err);
    }
  };

  const loadMyRegistrations = async () => {
    try {
      const data = await eventsService.getMyRegistrations();
      setMyRegistrations(data);
    } catch (err) {
      console.error('Error loading my registrations:', err);
    }
  };

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');
    try {
      const newEvent = await eventsService.createEvent({
        title: createForm.title,
        description: createForm.description,
        category: createForm.category,
        event_type: createForm.event_type,
        start_date: createForm.start_date,
        end_date: createForm.end_date,
        location: createForm.location || undefined,
        virtual_link: createForm.virtual_link || undefined,
        external_url: createForm.external_url || undefined,
        is_external: createForm.is_external,
        price: createForm.price ? parseFloat(createForm.price) : 0,
        max_attendees: createForm.max_attendees ? parseInt(createForm.max_attendees) : undefined,
        image_url: createForm.image_url || undefined,
        banner_url: createForm.banner_url || undefined,
        tags: createForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        status: createForm.status,
      });

      // Update local state immediately
      const processedEvent = {
        ...newEvent,
        organizer: newEvent.profiles,
        attendees_count: 0,
        is_registered: false
      };
      
      setMyEvents(prev => [processedEvent, ...prev]);
      
      if (createForm.status === 'published') {
        setEvents(prev => [processedEvent, ...prev]);
      }
      
      setShowCreateModal(false);
      setCreateForm({
        title: '',
        description: '',
        category: '' as ArtCategory,
        event_type: 'exhibition',
        start_date: '',
        end_date: '',
        location: '',
        virtual_link: '',
        external_url: '',
        is_external: false,
        price: '',
        max_attendees: '',
        image_url: '',
        banner_url: '',
        tags: '',
        status: 'draft',
      });
      setCreateError('');
    } catch (err) {
      if (err instanceof ContentModerationError) {
        const key = err.categories.includes('sexual')
          ? 'moderation.block.sexual'
          : err.categories.includes('profanity')
            ? 'moderation.block.profanity'
            : 'moderation.block.generic';
        setCreateError(t(key, 'Your content violates our community guidelines.'));
      } else {
        console.error('Error creating event:', err);
        setCreateError(select({ es: 'Error al crear el evento.', en: 'Error creating event.' }));
      }
    }
  };

  const handleRegisterForEvent = async (eventId: string) => {
    try {
      const registration = await eventsService.registerForEvent(eventId);
      
      // Update events list immediately
      setEvents(prev => prev.map(event => 
        event.id === eventId 
          ? { 
              ...event, 
              is_registered: true,
              attendees_count: (event.attendees_count || 0) + 1
            }
          : event
      ));
      
      // Update registrations list
      const eventData = events.find(e => e.id === eventId);
      if (eventData) {
        setMyRegistrations(prev => [...prev, {
          ...registration,
          events: eventData
        }]);
      }
    } catch (err) {
      console.error('Error registering for event:', err);
    }
  };

  const handleUnregisterFromEvent = async (eventId: string) => {
    try {
      await eventsService.updateEventRegistrationStatus(eventId, 'cancelled');
      
      // Update events list immediately
      setEvents(prev => prev.map(event => 
        event.id === eventId 
          ? { 
              ...event, 
              is_registered: false,
              attendees_count: Math.max(0, (event.attendees_count || 1) - 1)
            }
          : event
      ));
      
      // Remove from registrations list
      setMyRegistrations(prev => prev.filter(reg => reg.event_id !== eventId));
    } catch (err) {
      console.error('Error unregistering from event:', err);
    }
  };

  const getEventTypeLabel = (type: string) => select(eventTypeLabels[type] || { es: type, en: type });

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const formatPrice = (price: number) =>
    price === 0
      ? select({ es: 'Gratis', en: 'Free' })
      : new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: 'EUR',
        }).format(price);
  

  const EventCard: React.FC<{ event: Event; showActions?: boolean }> = ({ event, showActions = true }) => (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      {(event.banner_url || event.image_url) && (
        <img
          src={event.banner_url || event.image_url}
          alt={event.title}
          className="w-full h-48 object-cover"
        />
      )}
      
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-2">{event.title}</h3>
            <div className="flex items-center gap-3 mb-2">
              <span className="px-2 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">
                {categoryLabels[event.category] || event.category}
              </span>
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded-full">
                {getEventTypeLabel(event.event_type)}
              </span>
            </div>
          </div>
          <span className="text-lg font-bold text-green-600">
            {formatPrice(event.price)}
          </span>
        </div>

        <p className="text-gray-700 mb-4 line-clamp-2">{event.description}</p>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar size={16} />
            <span>{formatDate(event.start_date)}</span>
          </div>
          
          {event.location && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPin size={16} />
              <span>{event.location}</span>
            </div>
          )}
          
          {event.event_type === 'online' && event.virtual_link && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Video size={16} />
              <span>{select({ es: 'Evento online', en: 'Online event' })}</span>
            </div>
          )}
          
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <Users size={16} />
              <span>
                {select({
                  es: `${event.attendees_count || 0} asistentes`,
                  en: `${event.attendees_count || 0} attendees`,
                })}
              </span>
            </div>
            
            {event.organizer && (
              <div className="flex items-center gap-1">
                <img
                  src={event.organizer.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=24&h=24&fit=crop'}
                  alt={event.organizer.full_name}
                  className="w-4 h-4 rounded-full object-cover"
                />
                <span>{event.organizer.full_name}</span>
              </div>
            )}
          </div>
        </div>

        {event.tags && event.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {event.tags.map((tag, index) => (
              <span key={index} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                #{tag}
              </span>
            ))}
          </div>
        )}

        {showActions && (
          <div className="flex gap-3">
            <button
              onClick={() => {
                setSelectedEvent(event);
                setShowEventDetails(true);
              }}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {select({ es: 'Ver detalles', en: 'View details' })}
            </button>
            
            {event.organizer?.id !== user?.id && (
              <>
                {event.is_external && event.external_url ? (
                  <button
                    onClick={() => window.open(event.external_url, '_blank')}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {select({ es: 'Ir al evento', en: 'Go to event' })}
                  </button>
                ) : (
                  <button
                    onClick={() => event.is_registered ? handleUnregisterFromEvent(event.id) : handleRegisterForEvent(event.id)}
                    disabled={event.is_external}
                    className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                      event.is_registered
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : event.is_external 
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {event.is_external 
                      ? select({ es: 'Ver evento externo', en: 'View external event' })
                      : event.is_registered 
                        ? select({ es: 'Cancelar registro', en: 'Cancel registration' })
                        : select({ es: 'Registrarse', en: 'Register' })
                    }
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Calendar className="text-purple-600" size={24} />
          {select({ es: 'Eventos artísticos', en: 'Art events' })}
        </h1>
        <button
          onClick={() => {
            setCreateError('');
            setShowCreateModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus size={16} />
          {select({ es: 'Crear evento', en: 'Create event' })}
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('browse')}
            className={`py-2 px-1 border-b-2 transition-colors ${
              activeTab === 'browse'
                ? 'border-purple-500 text-purple-600 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {select({ es: 'Explorar eventos', en: 'Browse events' })}
          </button>
          <button
            onClick={() => setActiveTab('my-events')}
            className={`py-2 px-1 border-b-2 transition-colors ${
              activeTab === 'my-events'
                ? 'border-purple-500 text-purple-600 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {select({ es: 'Mis eventos', en: 'My events' })}
          </button>
          <button
            onClick={() => setActiveTab('my-registrations')}
            className={`py-2 px-1 border-b-2 transition-colors ${
              activeTab === 'my-registrations'
                ? 'border-purple-500 text-purple-600 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {select({ es: 'Mis registros', en: 'My registrations' })}
          </button>
        </nav>
      </div>

      {/* Filters (only for browse tab) */}
      {activeTab === 'browse' && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={16} className="text-gray-500" />
            <span className="font-medium text-gray-700">{select({ es: 'Filtros', en: 'Filters' })}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <select
                value={filters.category}
                onChange={(e) => setFilters(prev => ({...prev, category: e.target.value as ArtCategory | 'all'}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">{select({ es: 'Todas las categorías', en: 'All categories' })}</option>
                {Object.entries(categoryLabels).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <select
                value={filters.eventType}
                onChange={(e) => setFilters(prev => ({...prev, eventType: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">{select({ es: 'Todos los tipos', en: 'All types' })}</option>
                {Object.keys(eventTypeLabels).map((key) => (
                  <option key={key} value={key}>{getEventTypeLabel(key)}</option>
                ))}
              </select>
            </div>

            <div>
              <input
                type="text"
                value={filters.location}
                onChange={(e) => setFilters(prev => ({...prev, location: e.target.value}))}
                placeholder={select({ es: 'Ubicación...', en: 'Location...' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <select
                value={filters.priceRange}
                onChange={(e) => setFilters(prev => ({...prev, priceRange: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">{select({ es: 'Cualquier precio', en: 'Any price' })}</option>
                <option value="free">{select({ es: 'Gratis', en: 'Free' })}</option>
                <option value="0-25">€0 - €25</option>
                <option value="25-50">€25 - €50</option>
                <option value="50-100">€50 - €100</option>
                <option value="100+">€100+</option>
              </select>
            </div>
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filters.upcoming}
                  onChange={(e) => setFilters(prev => ({...prev, upcoming: e.target.checked}))}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700">
                  {select({ es: 'Solo próximos', en: 'Upcoming only' })}
                </span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-gray-200 h-64 rounded-xl"></div>
          ))}
        </div>
      ) : (
        <>
          {activeTab === 'browse' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {events.map(event => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          )}

          {activeTab === 'my-events' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {myEvents.map(event => (
                <EventCard key={event.id} event={event} showActions={false} />
              ))}
            </div>
          )}

          {activeTab === 'my-registrations' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {myRegistrations.map(registration => (
                <EventCard key={registration.id} event={registration.events} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Create Event Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Crear Evento</h2>
                <button
                  onClick={() => {
                    setCreateError('');
                    setShowCreateModal(false);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleCreateEvent} className="space-y-6">
                {createError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {createError}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {select({ es: 'Título del evento', en: 'Event title' })}
                    </label>
                    <input
                      type="text"
                      required
                      value={createForm.title}
                      onChange={(e) => setCreateForm({...createForm, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder={select({
                        es: 'Exposición de arte moderno...',
                        en: 'Modern art exhibition...',
                      })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {select({ es: 'Categoría', en: 'Category' })}
                    </label>
                    <select
                      required
                      value={createForm.category}
                      onChange={(e) => setCreateForm({...createForm, category: e.target.value as ArtCategory})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">{select({ es: 'Selecciona una categoría', en: 'Select a category' })}</option>
                      {Object.entries(categoryLabels).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {select({ es: 'Tipo de evento', en: 'Event type' })}
                  </label>
                  <select
                    required
                    value={createForm.event_type}
                    onChange={(e) => setCreateForm({...createForm, event_type: e.target.value as 'exhibition' | 'workshop' | 'concert' | 'meetup' | 'online'})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {Object.keys(eventTypeLabels).map((key) => (
                      <option key={key} value={key}>{getEventTypeLabel(key)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {select({ es: 'Descripción', en: 'Description' })}
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={createForm.description}
                    onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    placeholder={select({
                      es: 'Describe tu evento, qué pueden esperar los asistentes...',
                      en: 'Describe your event and what attendees can expect...',
                    })}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {select({ es: 'Fecha y hora de inicio', en: 'Start date & time' })}
                    </label>
                    <input
                      type="datetime-local"
                      required
                      value={createForm.start_date}
                      onChange={(e) => setCreateForm({...createForm, start_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {select({ es: 'Fecha y hora de fin', en: 'End date & time' })}
                    </label>
                    <input
                      type="datetime-local"
                      required
                      value={createForm.end_date}
                      onChange={(e) => setCreateForm({...createForm, end_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {createForm.event_type !== 'online' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {select({ es: 'Ubicación', en: 'Location' })}
                    </label>
                    <input
                      type="text"
                      value={createForm.location}
                      onChange={(e) => setCreateForm({...createForm, location: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder={select({ es: 'Dirección del evento', en: 'Event address' })}
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {select({ es: 'Enlace virtual', en: 'Virtual link' })}
                    </label>
                    <input
                      type="url"
                      value={createForm.virtual_link}
                      onChange={(e) => setCreateForm({...createForm, virtual_link: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="https://zoom.us/j/..."
                    />
                  </div>
                )}

                <div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-gray-900 mb-3">Tipo de registro</h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          id="internalEvent"
                          name="eventRegistration"
                          checked={!createForm.is_external}
                          onChange={() => setCreateForm({...createForm, is_external: false})}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <label htmlFor="internalEvent" className="text-sm font-medium text-gray-700">
                          Registro interno (los usuarios se registran en SoArtistic)
                        </label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          id="externalEvent"
                          name="eventRegistration"
                          checked={createForm.is_external}
                          onChange={() => setCreateForm({...createForm, is_external: true})}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <label htmlFor="externalEvent" className="text-sm font-medium text-gray-700">
                          Evento externo (redirigir a otra plataforma)
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {createForm.is_external && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <div className="flex items-start gap-2">
                        <div className="text-blue-600 mt-0.5">ℹ️</div>
                        <div>
                          <h5 className="font-medium text-blue-900 mb-1">
                            {select({ es: 'Evento externo', en: 'External event' })}
                          </h5>
                          <p className="text-sm text-blue-700 mb-3">
                            {select({
                              es: 'Los usuarios serán redirigidos a tu plataforma externa para registrarse (Eventbrite, Facebook, etc.)',
                              en: 'Users will be redirected to your external platform to register (Eventbrite, Facebook, etc.)',
                            })}
                          </p>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {select({ es: 'URL de registro externo *', en: 'External registration URL *' })}
                            </label>
                            <input
                              type="url"
                              required={createForm.is_external}
                              value={createForm.external_url}
                              onChange={(e) => setCreateForm({...createForm, external_url: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              placeholder={select({
                                es: 'https://eventbrite.com/e/mi-evento...',
                                en: 'https://eventbrite.com/e/my-event...',
                              })}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {!createForm.is_external && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <div className="flex items-start gap-2">
                        <div className="text-green-600 mt-0.5">✅</div>
                        <div>
                          <h5 className="font-medium text-green-900 mb-1">
                            {select({ es: 'Registro interno', en: 'Internal registration' })}
                          </h5>
                          <p className="text-sm text-green-700">
                            {select({
                              es: 'Los usuarios podrán registrarse directamente desde SoArtistic. Podrás ver la lista de asistentes y gestionar el evento.',
                              en: 'Users can register directly on SoArtistic. You will be able to see the attendee list and manage the event.',
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {select({ es: 'Precio (EUR)', en: 'Price (EUR)' })}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={createForm.price}
                        onChange={(e) => setCreateForm({...createForm, price: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {select({ es: 'Máximo de asistentes', en: 'Maximum attendees' })}
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={createForm.max_attendees}
                        onChange={(e) => setCreateForm({...createForm, max_attendees: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder={select({ es: 'Ilimitado', en: 'Unlimited' })}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {select({ es: 'Imagen/miniatura del evento', en: 'Event thumbnail image' })}
                    </label>
                    <input
                      type="url"
                      value={createForm.image_url}
                      onChange={(e) => setCreateForm({...createForm, image_url: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="https://..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {select({ es: 'Banner del evento (opcional)', en: 'Event banner (optional)' })}
                    </label>
                    <input
                      type="url"
                      value={createForm.banner_url}
                      onChange={(e) => setCreateForm({...createForm, banner_url: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {select({ es: 'Tags (separados por comas)', en: 'Tags (comma separated)' })}
                  </label>
                  <input
                    type="text"
                    value={createForm.tags}
                    onChange={(e) => setCreateForm({...createForm, tags: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder={select({ es: 'arte, moderno, exposición', en: 'art, modern, exhibition' })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {select({ es: 'Estado', en: 'Status' })}
                  </label>
                  <select
                    value={createForm.status}
                    onChange={(e) => setCreateForm({...createForm, status: e.target.value as 'draft' | 'published'})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="draft">{select({ es: 'Borrador', en: 'Draft' })}</option>
                    <option value="published">{select({ es: 'Publicar ahora', en: 'Publish now' })}</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {select({ es: 'Cancelar', en: 'Cancel' })}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    {select({ es: 'Crear evento', en: 'Create event' })}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Event Details Modal */}
     {showEventDetails && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {select({ es: 'Detalles del evento', en: 'Event details' })}
                </h2>
                <button
                  onClick={() => setShowEventDetails(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <EventCard event={selectedEvent} showActions={false} />
              
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setShowEventDetails(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {select({ es: 'Cerrar', en: 'Close' })}
                </button>
                {selectedEvent.organizer?.id !== user?.id && (
                  <button
                    onClick={() => {
                      if (selectedEvent.is_registered) {
                        void handleUnregisterFromEvent(selectedEvent.id);
                      } else {
                        void handleRegisterForEvent(selectedEvent.id);
                      }
                      setShowEventDetails(false);
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                      selectedEvent.is_registered
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {selectedEvent.is_registered
                      ? select({ es: 'Cancelar registro', en: 'Cancel registration' })
                      : select({ es: 'Registrarse', en: 'Register' })}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsView;
